import * as React from "react";
import {
  List,
  DataTable,
  TextField,
  ReferenceField,
  ReferenceInput,
  SelectArrayInput,
  AutocompleteInput,
  DateField,
  FunctionField,
  SearchInput,
  usePermissions,
  Pagination,
  useRecordContext,
  Button,
  DataProviderContext,
  useNotify,
  useRefresh,
  Confirm,
  DateInput,
} from "react-admin";
import { RESTAURANT_TYPES } from "../enums/RestaurantTypes";
import { REWARD_STATUS } from "../enums/RewardStatus";
import { ROLE_TYPES } from "../enums/Roles";
import RewardKanbanContent from "./RewardKanbanContent";
import { getRewardStatusName } from "../utils/RewardUtils";
import DeliveryCodeInput from "./DeliveryCodeInput";

const RewardFilter = () => {
  const { permissions } = usePermissions();
  return [
    ...(permissions === ROLE_TYPES.ADMIN
      ? [
          <SearchInput
            source="deliveryCode"
            placeholder="Buscar por Código"
            alwaysOn
          />,
          <ReferenceInput
            source="customer"
            reference="users"
            filter={{
              active: true,
              role: ROLE_TYPES.CUSTOMER,
            }}
            label="Usuario"
            alwaysOn
          >
            <AutocompleteInput
              filterToQuery={(searchText) => ({ email: searchText })}
              label="Usuario"
              optionText="email"
            />
          </ReferenceInput>,
          <SelectArrayInput
            optionText="name"
            label="Estado"
            source="status"
            choices={[
              {
                id: REWARD_STATUS.PENDING_DELIVERY,
                name: getRewardStatusName(REWARD_STATUS.PENDING_DELIVERY),
              },
              {
                id: REWARD_STATUS.DELIVERED,
                name: getRewardStatusName(REWARD_STATUS.DELIVERED),
              },
            ]}
          />,
          <ReferenceInput
            label="Restaurante"
            source="restaurant"
            reference="restaurants"
            perPage={100}
            filter={{
              type: RESTAURANT_TYPES.REGULAR,
            }}
            filterToQuery={(searchText) => ({ name: searchText })}
          >
            <AutocompleteInput
              filterToQuery={(searchText) => ({ name: searchText })}
              label="Restaurante"
              optionText="name"
            />
          </ReferenceInput>,
          <DateInput
            source="createdAtFrom"
            placeholder="Fecha Desde"
            label="Fecha Desde"
          />,
          <DateInput
            source="createdAtTo"
            placeholder="Fecha Hasta"
            label="Fecha Hasta"
          />,
        ]
      : []),
  ];
};

const ProductField = () => {
  const reward = useRecordContext();
  const productIndex = reward?.restaurant?.rewards?.findIndex(
    (rr) => rr._id === reward?.restaurantReward
  );

  if (productIndex === undefined) return null;

  return (
    <ReferenceField
      label="Producto"
      reference="products"
      source={`restaurant.rewards[${productIndex}].product`}
    >
      <TextField source="name" label="Nombre" />
    </ReferenceField>
  );
};

const RewardList = () => {
  const { permissions } = usePermissions();

  const dataProvider = React.useContext(DataProviderContext);
  const notify = useNotify();
  const refresh = useRefresh();

  const [loading, setLoading] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [rewardToDeliver, setRewardToDeliver] = React.useState();

  const DeliverButton = () => {
    const record = useRecordContext();
    if (record.status !== REWARD_STATUS.PENDING_DELIVERY) return;

    const onDeliverPress = (event) => {
      event.stopPropagation();
      setShowConfirm(true);
      setRewardToDeliver(record);
    };

    return <Button label="Entregar" onClick={onDeliverPress} />;
  };

  const handleConfirm = async () => {
    if (!rewardToDeliver) return;

    try {
      setLoading(true);
      const deliveredReward = await dataProvider.deliverReward(
        "redeemed-rewards",
        { id: rewardToDeliver._id, deliveryCode: rewardToDeliver.deliveryCode }
      );
      if (deliveredReward) {
        notify("¡Premio entregado con éxito!", {
          type: "success",
        });
      }
    } catch (error) {
      notify("Ha ocurrido un error al entregar el premio", { type: "error" });
    } finally {
      setLoading(false);
      setShowConfirm(false);
      setRewardToDeliver(null);
      refresh();
    }
  };

  const handleDialogClose = () => {
    setRewardToDeliver(null);
    setShowConfirm(false);
  };

  return (
    <List
      title="Premios"
      filters={RewardFilter()}
      pagination={
        permissions === ROLE_TYPES.RESTAURANT ? false : <Pagination />
      }
      exporter={false}
    >
      {permissions === ROLE_TYPES.ADMIN ? (
        <>
          <DataTable bulkActionButtons={false}>
            <DataTable.Col source="customer._id" label="Usuario">
              <ReferenceField
                label="Usuario"
                reference="users"
                source="customer._id"
                link={false}
              >
                <FunctionField
                  source="firstName"
                  render={(record) =>
                    `${record?.firstName || ""} ${record?.lastName || ""}` ||
                    "-"
                  }
                />
              </ReferenceField>
            </DataTable.Col>
            <DataTable.Col label="Producto">
              <ProductField label="Producto" />
            </DataTable.Col>
            <DataTable.Col source="restaurant._id" label="Restaurante">
              <ReferenceField
                label="Restaurante"
                reference="restaurants"
                source="restaurant._id"
              >
                <TextField source="name" label="Nombre" />
              </ReferenceField>
            </DataTable.Col>
            <DataTable.Col source="status" label="Estado">
              <FunctionField
                source="status"
                label="Estado"
                render={(record) => getRewardStatusName(record.status)}
              />
            </DataTable.Col>
            <DataTable.Col source="deliveryCode" label="Código" />
            {/* TODO: change to deliveryDate/completedDate */}
            <DataTable.Col source="updatedAt" label="Fecha">
              <DateField
                source="updatedAt"
                label="Fecha"
                showTime={true}
                emptyText="-"
              />
            </DataTable.Col>
            <DataTable.Col>
              <DeliverButton />
            </DataTable.Col>
          </DataTable>
          <Confirm
            isOpen={showConfirm}
            loading={loading}
            title={`Entregar premio`}
            content="¿Estás seguro de entregar este premio?"
            onConfirm={handleConfirm}
            onClose={handleDialogClose}
          />
        </>
      ) : (
        <>
          <DeliveryCodeInput />
          <RewardKanbanContent />
        </>
      )}
    </List>
  );
};

export default RewardList;
